import { Injectable } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { filter, map, exhaustMap, withLatestFrom, mapTo } from 'rxjs/operators';
import {
	RailroadDetailPartialState,
	railroadDetailFeatureKey,
} from '../../+state/railroad-detail.reducer';
import { Store, select } from '@ngrx/store';
import { CustomerClient } from 'src/shared/api';
import * as RailroadLogoActions from './railroad-logo.actions';
import * as RailroadDetailActions from '../../+state/railroad-detail.actions';
import * as RailroadDetailSelectors from '../../+state/railroad-detail.selectors';
import { isValidFile, isInvalidFile } from './railroad-logo.utility';
import { RailroadLogoUploadService } from './railroad-logo-upload.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DataPersistence } from '@nrwl/angular';

@Injectable()
export class RailroadLogoEffects {
	loadLogoFromStore$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RailroadDetailActions.railroadSelected),
			map((action) => action.railroadId),
			filter(Boolean),
			withLatestFrom(
				this.store.pipe(
					select(RailroadDetailSelectors.getSelectedRailroad)
				)
			),
			filter(([_id, railroad]) => !!railroad && !!railroad.logo),
			map(([_id, railroad]) => railroad.logo),
			map((logo) => RailroadLogoActions.logoLoadedFromStore({ logo }))
		)
	);

	loadLogoFromApi$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RailroadDetailActions.railroadSelected),
			map((action) => action.railroadId),
			filter<number>(Boolean),
			withLatestFrom(
				this.store.pipe(
					select(RailroadDetailSelectors.getSelectedRailroad)
				)
			),
			filter(([_id, railroad]) => !railroad || !railroad.logo),
			exhaustMap(([id, _railroad]) =>
				this.customerClient.getLogoForRailroad(id)
			),
			map((logo) => RailroadLogoActions.logoLoadedFromApi({ logo }))
		)
	);

	processValidLogoUpload$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(RailroadLogoActions.logoUploaded),
				map((action) => action.event.target.files[0]),
				filter(isValidFile),
				map((file) => this.railroadLogoService.readAsArrayBuffer(file))
			),
		{ dispatch: false }
	);

	notifyOfInvalidLogoUpload$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(RailroadLogoActions.logoUploaded),
				map((action) => action.event.target.files[0]),
				filter(isInvalidFile),
				exhaustMap(() =>
					this.snackBar
						.open('Only JPEG or PNG file types supported.', 'X')
						.onAction()
				),
				map(() => this.snackBar.dismiss())
			),
		{ dispatch: false }
	);

	uploadLogo$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RailroadLogoActions.logoSuccessfullyBuffered),
			map((action) => action.logo),
			filter<string>(Boolean),
			withLatestFrom(
				this.store.pipe(
					select(RailroadDetailSelectors.getSelectedRailroadId)
				)
			),
			map(([logo, railroadId]) => ({
				parentId: railroadId,
				attribute: [],
				data: logo,
			})),
			exhaustMap((request) =>
				this.customerClient.updateLogoForRailroad(request)
			),
			mapTo(RailroadLogoActions.logoSuccessfullyUploadedToApi())
		)
	);

	savePessimistic$ = createEffect(() =>
		this.dataPersistence.pessimisticUpdate(
			RailroadLogoActions.userClickedRemoveLogo,
			{
				run: (
					_action: ReturnType<
						typeof RailroadLogoActions.userClickedRemoveLogo
					>,
					state: RailroadDetailPartialState
				) => {
					const railroadId =
						state[railroadDetailFeatureKey].selectedRailroadId;
					return this.customerClient
						.removeLogoFromRailroad(railroadId)
						.pipe(mapTo(RailroadLogoActions.logoRemoved()));
				},
				onError: (_a: any, error: any) => {
					throw new Error(error);
				},
			}
		)
	);

	constructor(
		private readonly actions$: Actions,
		private readonly store: Store<RailroadDetailPartialState>,
		private readonly dataPersistence: DataPersistence<
			RailroadDetailPartialState
		>,
		private readonly railroadLogoService: RailroadLogoUploadService,
		private readonly customerClient: CustomerClient,
		private readonly snackBar: MatSnackBar
	) {}
}
